import usersApi from '@/api/v1/users';
import md5 from 'md5';

export default {
  namespaced: true,

  state: {
    users: [],
    usersSimplifiedList: [],
    backendVersion: '',
  },
  mutations: {
    SET_USERS(state, users) {
      state.users = users;
    },

    SET_USERS_SIMPLIFIED_LIST(state, users) {
      state.usersSimplifiedList = users;
    },

    UPDATE_USER(state, editedUser) {
      state.users = state.users.map((user) => {
        if (user.id === editedUser.id) return editedUser;
        return user;
      });
    },

    SET_BACKEND_VERSION(state, versionHash) {
      state.backendVersion = versionHash;
    },
  },

  actions: {
    fetchUsers({ commit, dispatch, rootState }) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
      const { selectedFilters, order } = rootState.filters;
      const { itemsPerPage, pagination } = rootState.pagination;
      const params = {
        page: pagination.page,
        itemsPerPage,
        order,
        filters: selectedFilters,
      };

      usersApi.index(params)
        .then((response) => {
          commit('SET_USERS', response.data.collection);
          dispatch('pagination/setPagination', response.data.pagination, { root: true });
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
      },

    fetchSimplifiedUsersList({ commit }, filters) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      usersApi.getSimpleIndex({ filters })
        .then((response) => {
          commit('SET_USERS_SIMPLIFIED_LIST', response.data);
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
      },

    fetchBackendVersion({ commit }) {
      usersApi.getBackendVersion()
        .then((response) => {
          commit('SET_BACKEND_VERSION', response.data.versionHash);
        });
    },

    createUser({ commit, dispatch }, email) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      usersApi.create(email)
        .then(() => {
          dispatch('dialog/clearDialog', null, { root: true });

          dispatch('snackbar/showSnackbar', {
            message: ['Poprawnie dodano użytkownika'],
            type: 'success',
          }, { root: true });

          dispatch('fetchUsers');
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    editUserPassword({ commit, dispatch }, user) {
      return new Promise((resolve) => {
        commit('CHANGE_LOADING_STATUS', true, { root: true });
        const { id, ...password } = user;

        usersApi.update(id, password)
          .then(() => {
            dispatch('snackbar/showSnackbar', {
              message: ['Hasło zostało zmienione'],
              type: 'success',
            }, { root: true });
            resolve();
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
      });
    },

    editUserNotificationsSettings({ commit, dispatch }, user) {
      return new Promise((resolve, reject) => {
        commit('CHANGE_LOADING_STATUS', true, { root: true });
        const { id, label, value } = user;
        const params = { [label]: value };
        usersApi.update(id, params)
          .then((response) => {
            commit('auth/SET_USER_DATA', response.data, { root: true });

            dispatch('snackbar/showSnackbar', {
              message: [`Powiadomienia ${value ? '' : 'nie'} będą wysyłane`],
              type: 'success',
            }, { root: true });
          })
          .catch(() => reject())
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
      });
    },

    editUserRole({ commit, dispatch }, user) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
      const { id, ...params } = user;

      usersApi.updateUserData(id, params)
        .then((response) => {
          const { firstName, lastName } = response.data;
          const fullName = `${firstName || ''} ${lastName || ''}`;
          commit('UPDATE_USER', response.data);

          dispatch('dialog/clearDialog', null, { root: true });

          dispatch('snackbar/showSnackbar', {
            message: [`Uprawnienia użytkownika ${fullName} zostały zaktualizowane`],
            type: 'success',
          }, { root: true });
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    recoverUserPassword({ commit, dispatch }, email) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      usersApi.recoverUserPassword(email)
        .then(() => {
          dispatch('dialog/clearDialog', null, { root: true });

          dispatch('snackbar/showSnackbar', {
            message: ['Na podany adres email wysłano link do zmiany hasła'],
            type: 'success',
          }, { root: true });
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    deleteUser({ commit, dispatch }, user) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`;

      usersApi.delete(user.id)
      .then(() => {
        dispatch('snackbar/showSnackbar', {
          message: [`Usunięto użytkownika ${fullName}`],
          type: 'success',
        }, { root: true });

        dispatch('fetchUsers');
      })
      .finally(() => {
        commit('CHANGE_LOADING_STATUS', false, { root: true });
      });
    },

    createReplacement({ commit, dispatch }, replacement) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return usersApi.replacements.create(replacement)
        .then(() => {
          dispatch('snackbar/showSnackbar', {
            message: ['Dodano zastępstwo'],
            type: 'success',
          }, { root: true });

          dispatch('fetchUsers');
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    updateReplacement({ commit, dispatch }, replacement) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
      const { id, ...params } = replacement;

      return usersApi.replacements.update(id, params)
        .then(() => {
          dispatch('snackbar/showSnackbar', {
            message: ['Zaktualizowano zastępstwo'],
            type: 'success',
          }, { root: true });

          dispatch('fetchUsers');
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },
    deleteReplacement({ commit, dispatch }, replacementId) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
        return usersApi.replacements.delete(replacementId)
          .then(() => {
            dispatch('snackbar/showSnackbar', {
              message: ['Usunięto zastępstwo'],
              type: 'success',
            }, { root: true });

            dispatch('fetchUsers');
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
    },
  },
  getters: {
    getReplacementsArray(state) {
      return state.users
        .filter((user) => user.replacements.length)
        .map((user) => user.replacements.map((replacement) => {
          const fullName = `${user.firstName} ${user.lastName}`;
          return {
            name: fullName,
            start: replacement.from,
            end: replacement.to,
            timed: false,
            color: `#${ md5(fullName).slice(0, 6)}`,
          };
        }))
        .flat();
    },
  },
};
